import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DatePicker } from './ui/DatePicker';
import { todayYmd, formatTRDate } from '../utils/dateUtils';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import {
  Database, Archive, Trash2, Settings, RefreshCw, Calendar,
  FileText, Package, Clock, MapPin, Bell, HardDrive,
  AlertTriangle, CheckCircle2, Download, Upload, Eye, ShieldAlert,
  Users, RotateCcw, Eraser, X, FileSpreadsheet, ClipboardList, TreePine, Wrench, Camera, Shield
} from 'lucide-react';
import { API } from '../config';
// Sprint 4 F5a: rol/yetki/hiyerarşi panelleri taşındı → SettingsTab (Rol Yönetimi).

const DataManagementPanel = () => {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [archivedData, setArchivedData] = useState([]);
  const [selectedArchiveCollection, setSelectedArchiveCollection] = useState('');
  const [selectedArchiveItems, setSelectedArchiveItems] = useState([]);
  const [archiveYear, setArchiveYear] = useState(new Date().getFullYear() - 1);
  
  // Ön izleme dialog state
  const [previewDialog, setPreviewDialog] = useState({
    isOpen: false,
    item: null,
    collection: ''
  });
  
  // Yedekleme state'leri
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupDescription, setBackupDescription] = useState('');
  const [selectedBackupInfo, setSelectedBackupInfo] = useState(null);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [fullBackupLoading, setFullBackupLoading] = useState(false);
  const [fullBackupDescription, setFullBackupDescription] = useState('');
  const [fullRestoreFile, setFullRestoreFile] = useState(null);
  const [fullRestoreConfirmOpen, setFullRestoreConfirmOpen] = useState(false);
  const [fullRestoreConfirmText, setFullRestoreConfirmText] = useState('');
  
  // Manuel temizlik tarihi için state
  const [manualCleanupDate, setManualCleanupDate] = useState('');

  // Arşiv tarihleri için merkezi state
  const [archiveDates, setArchiveDates] = useState({
    daily_tasks: '',
    passive_areas: '',
    leave_requests: '',
    revisions: '',
    material_requests: '',
    messages: '',
    location_logs: '',
    task_media: '',
  });
  const setArchiveDate = (key) => (value) =>
    setArchiveDates((prev) => ({ ...prev, [key]: value }));
  
  // Veri versiyonları (kaydet / bu versiyona dön)
  const [versions, setVersions] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [newVersionLabel, setNewVersionLabel] = useState('');
  const [restoreVersionId, setRestoreVersionId] = useState(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [deleteVersionId, setDeleteVersionId] = useState(null);
  const [deleteVersionConfirmOpen, setDeleteVersionConfirmOpen] = useState(false);

  // 2. Onay sistemi için state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    description: '',
    warningText: '',
    actionText: '',
    onConfirm: null,
    type: 'danger' // 'danger' veya 'warning'
  });

  // Kalıcı Sil (Çift Uyarılı) Dialog State
  const [directDeleteDialog, setDirectDeleteDialog] = useState({
    isOpen: false,
    step: 1, // 1: İlk uyarı, 2: Yazılı onay
    category: '',
    categoryName: '',
    confirmText: ''
  });

  // Verileri yükle
  useEffect(() => {
    fetchData();
    fetchBackups();
    fetchVersions();
  }, []);

  // Versiyonları listele
  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API}/api/data-management/versions`);
      setVersions(res.data.versions || []);
    } catch (error) {
      console.error('Versiyonlar yüklenirken hata:', error);
    }
  };

  // Yeni versiyon oluştur (tüm veriyi kaydet)
  const createVersion = async () => {
    const label = (newVersionLabel || '').trim() || 'Versiyon';
    try {
      setVersionLoading(true);
      await axios.post(`${API}/api/data-management/versions`, { label });
      setMessage({ type: 'success', text: `"${label}" versiyonu oluşturuldu. İstediğinizde bu versiyona dönebilirsiniz.` });
      setNewVersionLabel('');
      fetchVersions();
    } catch (error) {
      let errMsg = error.response?.data?.detail ?? error.message ?? 'Versiyon oluşturulurken hata oluştu';
      if (Array.isArray(errMsg)) errMsg = errMsg.map((m) => m.msg || m).join('. ');
      setMessage({ type: 'error', text: typeof errMsg === 'string' ? errMsg : 'Versiyon oluşturulurken hata oluştu' });
    } finally {
      setVersionLoading(false);
    }
  };

  // Bu versiyona geri dön
  const restoreVersion = async (versionId) => {
    try {
      setVersionLoading(true);
      const res = await axios.post(`${API}/api/data-management/versions/${encodeURIComponent(versionId)}/restore`);
      setMessage({ type: 'success', text: res.data.message || 'Veri bu versiyona geri yüklendi. Sayfayı yenileyin.' });
      setRestoreConfirmOpen(false);
      setRestoreVersionId(null);
      fetchData();
      fetchVersions();
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Geri yükleme sırasında hata oluştu' });
    } finally {
      setVersionLoading(false);
    }
  };

  // Versiyonu sil
  const deleteVersion = async (versionId) => {
    try {
      setVersionLoading(true);
      await axios.delete(`${API}/api/data-management/versions/${encodeURIComponent(versionId)}`);
      setMessage({ type: 'success', text: 'Versiyon silindi' });
      setDeleteVersionConfirmOpen(false);
      setDeleteVersionId(null);
      fetchVersions();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Versiyon silinirken hata oluştu' });
    } finally {
      setVersionLoading(false);
    }
  };

  // Yedekleri listele
  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API}/api/data-management/backups`);
      setBackups(res.data.backups || []);
    } catch (error) {
      console.error('Yedekler yüklenirken hata:', error);
    }
  };

  // Yedek oluştur
  const createBackup = async () => {
    try {
      setBackupLoading(true);
      const res = await axios.post(`${API}/api/data-management/backups/create?description=${encodeURIComponent(backupDescription)}`);
      setMessage({ type: 'success', text: `Yedek oluşturuldu: ${res.data.total_records} kayıt (${res.data.size_mb} MB)` });
      setBackupDescription('');
      fetchBackups();
    } catch (error) {
      setMessage({ type: 'error', text: 'Yedek oluşturulurken hata oluştu' });
    } finally {
      setBackupLoading(false);
    }
  };

  // Yedek indir
  const downloadBackup = (filename) => {
    const link = document.createElement('a');
    link.href = `${API}/api/data-management/backups/download/${filename}`;
    link.target = '_blank';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Tam yedek oluştur ve indir
  const createFullBackupAndDownload = async () => {
    try {
      setFullBackupLoading(true);
      const res = await axios.post(`${API}/api/data-management/full-backup/create?description=${encodeURIComponent(fullBackupDescription)}`);
      const filename = res.data?.filename;
      if (filename) {
        const link = document.createElement('a');
        link.href = `${API}/api/data-management/full-backup/download/${filename}`;
        link.target = '_blank';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setMessage({ type: 'success', text: `Tam yedek oluşturuldu: ${res.data.size_mb} MB` });
      setFullBackupDescription('');
    } catch (error) {
      setMessage({ type: 'error', text: 'Tam yedek oluşturulurken hata oluştu' });
    } finally {
      setFullBackupLoading(false);
    }
  };

  // Tam yedekten veritabanını geri yükle
  const restoreFullBackup = async () => {
    if (!fullRestoreFile) {
      setMessage({ type: 'error', text: 'Lütfen bir tam yedek dosyası seçin' });
      return;
    }
    if (fullRestoreConfirmText !== 'YUKLE') {
      setMessage({ type: 'error', text: 'Onay metni yanlış. "YUKLE" yazmalısınız.' });
      return;
    }
    try {
      setFullBackupLoading(true);
      const formData = new FormData();
      formData.append('file', fullRestoreFile);
      const res = await axios.post(
        `${API}/api/data-management/full-backup/restore?mode=${restoreMode}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setMessage({ type: 'success', text: `Geri yükleme tamamlandı: ${res.data.total_restored} kayıt` });
      fetchData();
      setFullRestoreFile(null);
      setFullRestoreConfirmText('');
      setFullRestoreConfirmOpen(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Tam yedek geri yükleme sırasında hata oluştu' });
    } finally {
      setFullBackupLoading(false);
    }
  };

  // Yedek sil
  const deleteBackup = async (filename) => {
    try {
      setBackupLoading(true);
      await axios.delete(`${API}/api/data-management/backups/${filename}`);
      setMessage({ type: 'success', text: 'Yedek silindi' });
      fetchBackups();
    } catch (error) {
      setMessage({ type: 'error', text: 'Yedek silinirken hata oluştu' });
    } finally {
      setBackupLoading(false);
    }
  };

  // Yedekten geri yükle
  const restoreBackup = async (filename) => {
    try {
      setBackupLoading(true);
      const res = await axios.post(`${API}/api/data-management/backups/restore/${filename}?mode=${restoreMode}`);
      setMessage({ type: 'success', text: `Geri yükleme tamamlandı: ${res.data.total_restored} kayıt` });
      fetchData(); // İstatistikleri güncelle
    } catch (error) {
      setMessage({ type: 'error', text: 'Geri yükleme sırasında hata oluştu' });
    } finally {
      setBackupLoading(false);
    }
  };

  // Yedek bilgisi getir
  const getBackupInfo = async (filename) => {
    try {
      const res = await axios.get(`${API}/api/data-management/backups/info/${filename}`);
      setSelectedBackupInfo(res.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Yedek bilgisi alınamadı' });
    }
  };

  // Kalıcı Sil - İlk uyarı
  const openDirectDeleteDialog = (category, categoryName) => {
    setDirectDeleteDialog({
      isOpen: true,
      step: 1,
      category,
      categoryName,
      confirmText: ''
    });
  };

  // Kalıcı Sil - İkinci adıma geç
  const proceedToStep2 = () => {
    setDirectDeleteDialog(prev => ({ ...prev, step: 2 }));
  };

  // Kalıcı Sil - İşlemi gerçekleştir
  const executeDirectDelete = async () => {
    const { category, confirmText } = directDeleteDialog;
    
    if (confirmText !== 'SIFIRLA') {
      setMessage({ type: 'error', text: 'Onay metni yanlış. "SIFIRLA" yazmalısınız.' });
      return;
    }
    
    try {
      setActionLoading(true);
      const res = await axios.delete(
        `${API}/api/data-management/direct-delete/${category}?confirm_text=SIFIRLA`
      );
      
      setMessage({ 
        type: 'success', 
        text: `✅ ${res.data.total_deleted} kayıt kalıcı olarak silindi!` 
      });
      
      setDirectDeleteDialog({ isOpen: false, step: 1, category: '', categoryName: '', confirmText: '' });
      fetchData(); // İstatistikleri güncelle
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Silme işlemi sırasında hata oluştu' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const defaultStats = { stats: {}, archives: {} };
    const defaultSettings = {};
    try {
      const [statsRes, settingsRes, archivesRes] = await Promise.allSettled([
        axios.get(`${API}/api/data-management/stats`),
        axios.get(`${API}/api/data-management/settings`),
        axios.get(`${API}/api/data-management/all-archives`)
      ]);
      const statsData = statsRes.status === 'fulfilled' ? statsRes.value.data : null;
      const settingsData = settingsRes.status === 'fulfilled' ? settingsRes.value.data : null;
      const archivesData = archivesRes.status === 'fulfilled' ? archivesRes.value.data : null;
      setStats(statsData ? { ...statsData, archives: archivesData || statsData.archives || {} } : { ...defaultStats, archives: archivesData || {} });
      setSettings(settingsData || defaultSettings);
      if (statsRes.status === 'rejected' || settingsRes.status === 'rejected' || archivesRes.status === 'rejected') {
        console.warn('Veri yönetimi: bazı istekler başarısız', { stats: statsRes.status, settings: settingsRes.status, archives: archivesRes.status });
        setMessage({ type: 'warning', text: 'Bazı istatistikler yüklenemedi; Versiyonlar ve Yedekleme kullanılabilir.' });
      }
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      setStats(defaultStats);
      setSettings(defaultSettings);
      setMessage({ type: 'error', text: 'Veriler yüklenirken hata oluştu. Versiyonlar sekmesini kullanabilirsiniz.' });
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      setActionLoading(true);
      await axios.put(`${API}/api/data-management/settings`, newSettings);
      setSettings(newSettings);
      setMessage({ type: 'success', text: 'Ayarlar güncellendi' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Ayarlar güncellenirken hata oluştu' });
    } finally {
      setActionLoading(false);
    }
  };

  const archiveLeaveRequests = async () => {
    if (!window.confirm(`${archiveYear} yılının izin taleplerini arşivlemek istediğinize emin misiniz?`)) return;
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/api/data-management/archive/leave-requests?year=${archiveYear}`);
      setMessage({ type: 'success', text: res.data.message });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Arşivleme hatası: ' + error.response?.data?.detail });
    } finally {
      setActionLoading(false);
    }
  };

  const archiveMaterialRequests = async () => {
    if (!window.confirm('Reddedilen malzeme taleplerini arşivlemek istediğinize emin misiniz?')) return;
    try {
      setActionLoading(true);
      const days = settings?.rejected_material_archive_days || 90;
      const res = await axios.post(`${API}/api/data-management/archive/material-requests?days_old=${days}`);
      setMessage({ type: 'success', text: res.data.message });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Arşivleme hatası: ' + error.response?.data?.detail });
    } finally {
      setActionLoading(false);
    }
  };

  const archiveRevisions = async () => {
    if (!window.confirm('Tamamlanan revizeleri arşivlemek istediğinize emin misiniz?')) return;
    try {
      setActionLoading(true);
      const days = settings?.revision_archive_after_days || 180;
      const res = await axios.post(`${API}/api/data-management/archive/revisions?days_after_completion=${days}`);
      setMessage({ type: 'success', text: res.data.message });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Arşivleme hatası: ' + error.response?.data?.detail });
    } finally {
      setActionLoading(false);
    }
  };

  const cleanupLocationLogs = async () => {
    if (!window.confirm('Eski konum loglarını silmek istediğinize emin misiniz? Bu işlem geri alınamaz!')) return;
    try {
      setActionLoading(true);
      const days = settings?.location_logs_delete_days || 7;
      const res = await axios.delete(`${API}/api/data-management/cleanup/location-logs?days_old=${days}`);
      setMessage({ type: 'success', text: res.data.message });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Silme hatası: ' + error.response?.data?.detail });
    } finally {
      setActionLoading(false);
    }
  };

  const cleanupNotifications = async () => {
    if (!window.confirm('Eski bildirimleri silmek istediğinize emin misiniz?')) return;
    try {
      setActionLoading(true);
      const days = settings?.notifications_delete_days || 90;
      const res = await axios.delete(`${API}/api/data-management/cleanup/notifications?days_old=${days}`);
      setMessage({ type: 'success', text: res.data.message });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Silme hatası: ' + error.response?.data?.detail });
    } finally {
      setActionLoading(false);
    }
  };

  const runAutoCleanup = async (customDate = null) => {
    const dateInfo = customDate 
      ? `${new Date(customDate).toLocaleDateString('tr-TR')} tarihine göre` 
      : 'şimdi';
    if (!window.confirm(`Otomatik temizlik işlemini ${dateInfo} çalıştırmak istediğinize emin misiniz?`)) return;
    try {
      setActionLoading(true);
      const url = customDate 
        ? `${API}/api/data-management/run-auto-cleanup?reference_date=${customDate}`
        : `${API}/api/data-management/run-auto-cleanup`;
      const res = await axios.post(url);
      setMessage({ type: 'success', text: `Temizlik tamamlandı: ${JSON.stringify(res.data.results)}` });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Temizlik hatası: ' + error.response?.data?.detail });
    } finally {
      setActionLoading(false);
    }
  };

  const fetchArchivedData = async (collection = null) => {
    const targetCollection = collection || selectedArchiveCollection;
    if (!targetCollection) return;
    
    try {
      setActionLoading(true);
      setSelectedArchiveItems([]); // Seçimleri temizle
      const res = await axios.get(`${API}/api/data-management/archive-details/${targetCollection}?limit=100`);
      setArchivedData(res.data.items || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Arşiv verileri yüklenemedi' });
      setArchivedData([]);
    } finally {
      setActionLoading(false);
    }
  };

  const restoreFromArchive = async (itemId) => {
    if (!window.confirm('Bu kaydı arşivden geri yüklemek istediğinize emin misiniz?')) return;
    try {
      setActionLoading(true);
      await axios.post(`${API}/api/data-management/restore-from-archive/${selectedArchiveCollection}/${itemId}`);
      setMessage({ type: 'success', text: 'Kayıt geri yüklendi' });
      fetchArchivedData(selectedArchiveCollection);
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Geri yükleme hatası' });
    } finally {
      setActionLoading(false);
    }
  };

  const deleteOldArchives = async () => {
    const years = settings?.leave_delete_after_years || 2;
    if (!window.confirm(`${years} yıldan eski arşiv verilerini kalıcı olarak silmek istediğinize emin misiniz? BU İŞLEM GERİ ALINAMAZ!`)) return;
    try {
      setActionLoading(true);
      const res = await axios.delete(`${API}/api/data-management/permanent-delete/${selectedArchiveCollection}?confirm_text=KALICI SIL`);
      setMessage({ type: 'success', text: res.data.message || 'Arşiv silindi' });
      fetchArchivedData(selectedArchiveCollection);
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Silme hatası' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2">Yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center justify-between bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center gap-2">
          <Database className="h-6 w-6" />
          <h2 className="text-xl font-bold">VERİ YÖNETİMİ</h2>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchData}
          className="bg-white/10 border-white/30 text-white hover:bg-white/20"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Yenile
        </Button>
      </div>

      {/* Mesaj */}
      {message && (
        <Alert className={message.type === 'error' ? 'bg-red-50 border-red-200' : message.type === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}>
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : message.type === 'warning' ? 'text-amber-800' : 'text-green-800'}>
            {message.type === 'error' ? <AlertTriangle className="inline h-4 w-4 mr-2" /> : message.type === 'warning' ? <AlertTriangle className="inline h-4 w-4 mr-2" /> : <CheckCircle2 className="inline h-4 w-4 mr-2" />}
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="overview">📊 Genel Bakış</TabsTrigger>
          <TabsTrigger value="versions">📌 Versiyonlar</TabsTrigger>
          <TabsTrigger value="reports">📑 Raporlar</TabsTrigger>
          <TabsTrigger value="backup">💾 Yedekleme</TabsTrigger>
          <TabsTrigger value="archive">📁 Arşivleme</TabsTrigger>
          <TabsTrigger value="cleanup">🗑️ Temizlik</TabsTrigger>
          <TabsTrigger value="settings">⚙️ Ayarlar</TabsTrigger>
        </TabsList>

        {/* VERSİYONLAR - Tüm veriyi kaydet / Bu versiyona dön */}
        <TabsContent value="versions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-indigo-600" />
                Veri Versiyonları
              </CardTitle>
              <CardDescription>
                Mevcut tüm veriyi (parklar, personel, izinler, faaliyetler, revizeler vb. — MongoDB&#39;de varsa hepsi) bir versiyon olarak kaydedin. 
                İndirilen ZIP&#39;i harici diske kopyalayıp db_export&#39;a açınca birebir aynı veriyle çalışır. Değişiklik sonrası &quot;Bu versiyona dön&quot; ile geri dönebilirsiniz.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <Label>Versiyon adı (örn: versiyon 13032026)</Label>
                  <Input
                    placeholder="versiyon 13032026"
                    value={newVersionLabel}
                    onChange={(e) => setNewVersionLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createVersion()}
                  />
                </div>
                <Button onClick={createVersion} disabled={versionLoading}>
                  {versionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  Versiyon Oluştur (Kaydet)
                </Button>
              </div>
              <div>
                <Label className="text-sm font-medium">Kayıtlı versiyonlar</Label>
                <p className="text-xs text-gray-500 mb-2">ZIP indir: harici diske kopyalayın; ZIP içeriğini backend/uploads/db_export içine açın, USE_LOCAL_JSON=1 ile çalıştırın.</p>
                {versions.length === 0 ? (
                  <p className="text-gray-500 py-4">Henüz versiyon yok. Yukarıdan bir versiyon oluşturun.</p>
                ) : (
                  <Table>
                    <TableHeader>
                        <TableRow>
                        <TableHead>Versiyon adı</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Dosya sayısı</TableHead>
                        <TableHead className="text-right">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {versions.map((v) => (
                        <TableRow key={v.id}>
                          <TableCell className="font-medium">{v.label || v.id}</TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {v.created_at ? new Date(v.created_at).toLocaleString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell>{v.file_count ?? '-'}</TableCell>
                          <TableCell className="text-right">
                            <a
                              href={`${API}/api/data-management/versions/${encodeURIComponent(v.id)}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block"
                            >
                              <Button type="button" size="sm" variant="outline" className="mr-2">
                                <Download className="h-4 w-4 mr-1" />
                                ZIP indir
                              </Button>
                            </a>
                            <Button
                              size="sm"
                              variant="default"
                              className="mr-2"
                              onClick={() => { setRestoreVersionId(v.id); setRestoreConfirmOpen(true); }}
                              disabled={versionLoading}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Bu versiyona dön
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => { setDeleteVersionId(v.id); setDeleteVersionConfirmOpen(true); }}
                              disabled={versionLoading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
          {/* Geri yükleme onay dialog */}
          <Dialog open={restoreConfirmOpen} onOpenChange={setRestoreConfirmOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bu versiyona geri dön</DialogTitle>
                <DialogDescription>
                  Tüm mevcut veri (parklar, personel, izinler vb.) seçtiğiniz versiyonun anlık görüntüsü ile değiştirilecek. 
                  Bu işlem geri alınamaz. Devam etmek istiyor musunuz? İşlemden sonra sayfa yenilenecektir.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setRestoreConfirmOpen(false); setRestoreVersionId(null); }}>İptal</Button>
                <Button onClick={() => restoreVersionId && restoreVersion(restoreVersionId)} disabled={versionLoading}>
                  {versionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  Evet, bu versiyona dön
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Versiyon silme onay dialog */}
          <Dialog open={deleteVersionConfirmOpen} onOpenChange={setDeleteVersionConfirmOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Versiyonu sil</DialogTitle>
                <DialogDescription>
                  Bu versiyon klasörü silinecek. Mevcut veriniz etkilenmez. Onaylıyor musunuz?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setDeleteVersionConfirmOpen(false); setDeleteVersionId(null); }}>İptal</Button>
                <Button variant="destructive" onClick={() => deleteVersionId && deleteVersion(deleteVersionId)} disabled={versionLoading}>
                  {versionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  Sil
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* GENEL BAKIŞ */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats?.stats && Object.entries(stats.stats).map(([key, stat]) => {
              const icons = {
                leave_requests: <Calendar className="h-6 w-6" />,
                material_requests: <Package className="h-6 w-6" />,
                revisions: <FileText className="h-6 w-6" />,
                task_media: <HardDrive className="h-6 w-6" />,
                location_logs: <MapPin className="h-6 w-6" />,
                notifications: <Bell className="h-6 w-6" />
              };
              const labels = {
                leave_requests: 'İzin Talepleri',
                material_requests: 'Malzeme Talepleri',
                revisions: 'Revizeler',
                task_media: 'Görev Medyaları',
                location_logs: 'Konum Logları',
                notifications: 'Bildirimler'
              };
              
              return (
                <Card key={key} className="text-center">
                  <CardContent className="pt-4">
                    <div className="text-blue-600 mb-2 flex justify-center">
                      {icons[key]}
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{labels[key]}</p>
                    <p className="text-2xl font-bold text-gray-800">{stat.active_count}</p>
                    {stat.archived_count > 0 && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        <Archive className="h-3 w-3 mr-1" />
                        {stat.archived_count} arşivde
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Son Temizlik Bilgisi */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">Son Otomatik Temizlik</p>
                  <p className="font-medium">
                    {stats?.last_cleanup_date 
                      ? new Date(stats.last_cleanup_date).toLocaleString('tr-TR')
                      : 'Henüz yapılmadı'}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
                  {/* Manuel Tarih Seçimi */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-500 whitespace-nowrap">📅 Temizlik Tarihi:</Label>
                    <div className="w-48">
                      <DatePicker
                        value={manualCleanupDate}
                        onChange={setManualCleanupDate}
                        max={todayYmd()}
                        placeholder="Tarih seç"
                      />
                    </div>
                  </div>
                  
                  {/* Butonlar */}
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => runAutoCleanup()} 
                      disabled={actionLoading}
                      variant="outline"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                      Şimdi Çalıştır
                    </Button>
                    
                    <Button 
                      onClick={() => runAutoCleanup(manualCleanupDate)} 
                      disabled={actionLoading || !manualCleanupDate}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Calendar className={`h-4 w-4 mr-2`} />
                      Seçilen Tarihe Göre
                    </Button>
                  </div>
                </div>
              </div>
              
              {manualCleanupDate && (
                <p className="text-xs text-blue-600 mt-2">
                  💡 {new Date(manualCleanupDate).toLocaleDateString('tr-TR')} tarihinden önceki eski kayıtlar temizlenecek
                </p>
              )}
            </CardContent>
          </Card>

          {/* Tam Yedek (Proje + Veritabani) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-blue-600" />
                Tam Yedek (Proje + Veritabanı)
              </CardTitle>
              <CardDescription>
                Bu yedek proje dosyalarını ve tüm veritabanı kayıtlarını içerir. 
                Başka bilgisayarda proje klasörünü açıp veritabanını buradan geri yükleyebilirsiniz.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full-backup-desc">Yedek Açıklaması (Opsiyonel)</Label>
                  <Input
                    id="full-backup-desc"
                    placeholder="Örn: Test öncesi tam yedek"
                    value={fullBackupDescription}
                    onChange={(e) => setFullBackupDescription(e.target.value)}
                  />
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={createFullBackupAndDownload}
                    disabled={fullBackupLoading}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Tam Yedek Oluştur ve İndir
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Tam Yedekten Veritabanı Geri Yükle</Label>
                  <Input
                    type="file"
                    accept=".zip"
                    onChange={(e) => setFullRestoreFile(e.target.files?.[0] || null)}
                  />
                  <SearchableSelect
                    value={restoreMode}
                    onChange={setRestoreMode}
                    showAllOption={false}
                    placeholder="Geri yükleme modu"
                    searchPlaceholder="Ara..."
                    options={[
                      { id: "merge", name: "Birleştir (Mevcutla birleştir)" },
                      { id: "replace", name: "Değiştir (Tüm verileri sil)" },
                    ]}
                  />
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => setFullRestoreConfirmOpen(true)}
                    disabled={fullBackupLoading || !fullRestoreFile}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Veritabanını Geri Yükle
                  </Button>
                  <p className="text-xs text-gray-500">
                    Not: Proje dosyalarını geri yüklemek için indirilen zip'i manuel olarak açın.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ARŞİVLEME */}
        <TabsContent value="archive" className="space-y-4">
          {/* Bilgi Mesajı */}
          <Alert className="bg-green-50 border-green-300">
            <Archive className="h-5 w-5 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>📁 Arşiv Yönetimi:</strong> Temizlik sekmesinden arşivlenen verileri burada görüntüleyebilir, 
              geri yükleyebilir veya kalıcı olarak silebilirsiniz.
            </AlertDescription>
          </Alert>

          {/* Arşiv Özeti Kartları */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { key: 'daily_task_completions_archive', name: 'Park Bakım', icon: TreePine, color: 'green' },
              { key: 'leave_requests_archive', name: 'İzin Talepleri', icon: Calendar, color: 'blue' },
              { key: 'material_requests_archive', name: 'Malzeme', icon: Package, color: 'purple' },
              { key: 'messages_archive', name: 'Mesajlar', icon: FileText, color: 'cyan' },
              { key: 'revisions_archive', name: 'Revizeler', icon: Wrench, color: 'orange' },
              { key: 'location_logs_archive', name: 'Konum', icon: MapPin, color: 'rose' },
              { key: 'task_media_archive', name: 'Medya', icon: Camera, color: 'amber' },
              { key: 'passive_area_activities_archive', name: 'Pasif Alan', icon: MapPin, color: 'gray' },
              { key: 'passive_area_inventory_history_archive', name: 'Envanter', icon: ClipboardList, color: 'slate' }
            ].map(archive => {
              const Icon = archive.icon;
              const archiveStats = stats?.archives?.[archive.key];
              const count = archiveStats?.count || 0;
              return (
                <Card 
                  key={archive.key} 
                  className={`cursor-pointer hover:shadow-md transition-shadow ${selectedArchiveCollection === archive.key ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => {
                    setSelectedArchiveCollection(archive.key);
                    fetchArchivedData(archive.key);
                  }}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 text-${archive.color}-600`} />
                      <span className="text-xs font-medium truncate">{archive.name}</span>
                    </div>
                    <div className="mt-1">
                      <span className={`text-lg font-bold ${count > 0 ? `text-${archive.color}-600` : 'text-gray-400'}`}>
                        {count}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">kayıt</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Arşiv Detay Görüntüleme */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Arşiv İçeriği
                  {selectedArchiveCollection && (
                    <Badge variant="outline" className="ml-2">
                      {selectedArchiveCollection.replace('_archive', '').replace(/_/g, ' ')}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex gap-2">
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => fetchArchivedData(selectedArchiveCollection)}
                    disabled={actionLoading || !selectedArchiveCollection}
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${actionLoading ? 'animate-spin' : ''}`} />
                    Yenile
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!selectedArchiveCollection ? (
                <div className="text-center py-8 text-gray-500">
                  <Archive className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Görüntülemek için yukarıdan bir arşiv kategorisi seçin</p>
                </div>
              ) : archivedData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Bu kategoride arşivlenmiş kayıt bulunmuyor</p>
                </div>
              ) : (
                <>
                  <div className="max-h-80 overflow-y-auto border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <input 
                              type="checkbox" 
                              className="rounded"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedArchiveItems(archivedData.map(item => item.id));
                                } else {
                                  setSelectedArchiveItems([]);
                                }
                              }}
                              checked={selectedArchiveItems.length === archivedData.length && archivedData.length > 0}
                            />
                          </TableHead>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Detay</TableHead>
                          <TableHead>Arşiv Tarihi</TableHead>
                          <TableHead className="text-right">İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {archivedData.map(item => (
                          <TableRow 
                            key={item.id} 
                            className="cursor-pointer hover:bg-blue-50"
                            onClick={(e) => {
                              // Checkbox veya buton tıklamalarını engelle
                              if (e.target.type === 'checkbox' || e.target.closest('button')) return;
                              setPreviewDialog({
                                isOpen: true,
                                item: item,
                                collection: selectedArchiveCollection
                              });
                            }}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox" 
                                className="rounded"
                                checked={selectedArchiveItems.includes(item.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedArchiveItems([...selectedArchiveItems, item.id]);
                                  } else {
                                    setSelectedArchiveItems(selectedArchiveItems.filter(id => id !== item.id));
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell className="text-sm">
                              {item.completion_date || item.created_at || item.timestamp || item.activity_date 
                                ? new Date(item.completion_date || item.created_at || item.timestamp || item.activity_date).toLocaleDateString('tr-TR') 
                                : '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {item.personnel_name || item.park_name || item.title || item.subject || item.sender_name || item.area_name || '-'}
                            </TableCell>
                            <TableCell className="text-xs text-gray-500">
                              {item.archived_at ? new Date(item.archived_at).toLocaleDateString('tr-TR') : '-'}
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex gap-1 justify-end">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="h-7 px-2"
                                  onClick={() => {
                                    setPreviewDialog({
                                      isOpen: true,
                                      item: item,
                                      collection: selectedArchiveCollection
                                    });
                                  }}
                                  title="Ön İzleme"
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 px-2"
                                  onClick={() => {
                                    setConfirmDialog({
                                      isOpen: true,
                                      title: '🔄 Geri Yükle',
                                      description: 'Bu kayıt arşivden çıkarılıp aktif listeye geri yüklenecek.',
                                      warningText: 'Kayıt tekrar aktif hale gelecek.',
                                      actionText: 'Geri Yükle',
                                      onConfirm: async () => {
                                        try {
                                          setActionLoading(true);
                                          await axios.post(`${API}/api/data-management/restore-from-archive/${selectedArchiveCollection}/${item.id}`);
                                          setMessage({ type: 'success', text: 'Kayıt geri yüklendi' });
                                          fetchArchivedData(selectedArchiveCollection);
                                          fetchData();
                                        } catch (error) {
                                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                                        } finally {
                                          setActionLoading(false);
                                        }
                                      },
                                      type: 'warning'
                                    });
                                  }}
                                  title="Geri Yükle"
                                >
                                  <Upload className="h-3 w-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => {
                                    setConfirmDialog({
                                      isOpen: true,
                                      title: '🗑️ Kalıcı Sil',
                                      description: 'Bu kayıt kalıcı olarak silinecek.',
                                      warningText: 'BU İŞLEM GERİ ALINAMAZ!',
                                      actionText: 'KALICI SİL',
                                      onConfirm: async () => {
                                        try {
                                          setActionLoading(true);
                                          await axios.delete(`${API}/api/data-management/permanent-delete/${selectedArchiveCollection}?confirm_text=KALICI SIL&item_ids=${item.id}`);
                                          setMessage({ type: 'success', text: 'Kayıt kalıcı olarak silindi' });
                                          fetchArchivedData(selectedArchiveCollection);
                                          fetchData();
                                        } catch (error) {
                                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                                        } finally {
                                          setActionLoading(false);
                                        }
                                      },
                                      type: 'danger'
                                    });
                                  }}
                                  title="Kalıcı Sil"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Toplu İşlemler */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-sm text-gray-500">
                      {selectedArchiveItems.length > 0 && (
                        <span>{selectedArchiveItems.length} kayıt seçildi</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={selectedArchiveItems.length === 0 || actionLoading}
                        onClick={() => {
                          setConfirmDialog({
                            isOpen: true,
                            title: '🔄 Seçilenleri Geri Yükle',
                            description: `${selectedArchiveItems.length} kayıt arşivden çıkarılıp aktif listeye geri yüklenecek.`,
                            warningText: 'Seçilen tüm kayıtlar aktif hale gelecek.',
                            actionText: 'Geri Yükle',
                            onConfirm: async () => {
                              try {
                                setActionLoading(true);
                                for (const itemId of selectedArchiveItems) {
                                  await axios.post(`${API}/api/data-management/restore-from-archive/${selectedArchiveCollection}/${itemId}`);
                                }
                                setMessage({ type: 'success', text: `${selectedArchiveItems.length} kayıt geri yüklendi` });
                                setSelectedArchiveItems([]);
                                fetchArchivedData(selectedArchiveCollection);
                                fetchData();
                              } catch (error) {
                                setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                              } finally {
                                setActionLoading(false);
                              }
                            },
                            type: 'warning'
                          });
                        }}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Seçilenleri Geri Yükle
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={selectedArchiveItems.length === 0 || actionLoading}
                        onClick={() => {
                          setConfirmDialog({
                            isOpen: true,
                            title: '🗑️ KALICI SİLME',
                            description: `${selectedArchiveItems.length} kayıt kalıcı olarak silinecek.`,
                            warningText: 'BU İŞLEM GERİ ALINAMAZ! Silinen veriler kurtarılamaz.',
                            actionText: 'KALICI SİL',
                            onConfirm: async () => {
                              try {
                                setActionLoading(true);
                                await axios.delete(`${API}/api/data-management/permanent-delete/${selectedArchiveCollection}?confirm_text=KALICI SIL&item_ids=${selectedArchiveItems.join(',')}`);
                                setMessage({ type: 'success', text: `${selectedArchiveItems.length} kayıt kalıcı olarak silindi` });
                                setSelectedArchiveItems([]);
                                fetchArchivedData(selectedArchiveCollection);
                                fetchData();
                              } catch (error) {
                                setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                              } finally {
                                setActionLoading(false);
                              }
                            },
                            type: 'danger'
                          });
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Seçilenleri Kalıcı Sil
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Tüm Arşivi Kalıcı Silme */}
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <ShieldAlert className="h-5 w-5" />
                Tehlikeli Bölge - Kalıcı Silme
              </CardTitle>
              <CardDescription className="text-red-600">
                Seçilen kategorideki TÜM arşiv kayıtlarını kalıcı olarak siler. Bu işlem geri alınamaz!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <SearchableSelect
                  value={selectedArchiveCollection || ''}
                  onChange={setSelectedArchiveCollection}
                  className="w-64"
                  showAllOption={false}
                  placeholder="Kategori seçin..."
                  searchPlaceholder="Kategori ara..."
                  options={[
                    { id: "daily_task_completions_archive", name: "Park Bakım Kayıtları" },
                    { id: "leave_requests_archive", name: "İzin Talepleri" },
                    { id: "material_requests_archive", name: "Malzeme Talepleri" },
                    { id: "messages_archive", name: "Mesajlar" },
                    { id: "revisions_archive", name: "Revizeler" },
                    { id: "location_logs_archive", name: "Konum Geçmişi" },
                    { id: "task_media_archive", name: "Görev Medyaları" },
                    { id: "passive_area_activities_archive", name: "Pasif Alan Faaliyetleri" },
                  ]}
                />
                <Button
                  variant="destructive"
                  disabled={!selectedArchiveCollection || actionLoading}
                  onClick={() => {
                    const archiveCount = stats?.archives?.[selectedArchiveCollection]?.count || 0;
                    setConfirmDialog({
                      isOpen: true,
                      title: '⚠️ TÜM ARŞİVİ KALICI SİL',
                      description: `"${selectedArchiveCollection.replace('_archive', '').replace(/_/g, ' ')}" kategorisindeki ${archiveCount} kayıt kalıcı olarak silinecek.`,
                      warningText: 'BU İŞLEM GERİ ALINAMAZ! Tüm arşivlenmiş veriler tamamen silinecek ve kurtarılamayacak.',
                      actionText: 'TÜM ARŞİVİ SİL',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.delete(`${API}/api/data-management/permanent-delete/${selectedArchiveCollection}?confirm_text=KALICI SIL`);
                          setMessage({ type: 'success', text: `${res.data.deleted_count} kayıt kalıcı olarak silindi` });
                          setArchivedData([]);
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'danger'
                    });
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Tüm Arşivi Kalıcı Sil
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEMİZLİK - YENİ TASARIM */}
        <TabsContent value="cleanup" className="space-y-6">
          {/* Bilgi Mesajı */}
          <Alert className="bg-blue-50 border-blue-300">
            <Archive className="h-5 w-5 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>📁 Arşivle + Sil:</strong> Veriler önce arşive taşınır, sonra aktif listeden silinir. 
              Arşivlenen verileri &quot;Arşivleme&quot; sekmesinden görüntüleyebilir ve geri yükleyebilirsiniz.
            </AlertDescription>
          </Alert>

          {/* Kategori Tabanlı Arşivleme */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Park Bakım Yönetimi */}
            <Card className="border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TreePine className="h-5 w-5 text-green-600" />
                  Park Bakım Yönetimi
                </CardTitle>
                <CardDescription>Günlük bakım kayıtlarını arşivle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih öncesi:</Label>
                  <div className="flex-1">
                    <DatePicker
                      value={archiveDates.daily_tasks}
                      onChange={setArchiveDate('daily_tasks')}
                      max={todayYmd()}
                      placeholder="Tarih seç"
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    const beforeDate = archiveDates.daily_tasks;
                    if (!beforeDate) {
                      setMessage({ type: 'error', text: 'Lütfen bir tarih seçin' });
                      return;
                    }
                    setConfirmDialog({
                      isOpen: true,
                      title: '📁 Park Bakım Kayıtlarını Arşivle',
                      description: `${formatTRDate(beforeDate)} tarihinden önceki tüm bakım kayıtları arşivlenecek.`,
                      warningText: 'Veriler arşive taşınacak ve aktif listeden silinecek. Arşivden geri yükleyebilirsiniz.',
                      actionText: 'Arşivle + Sil',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.post(`${API}/api/data-management/archive-by-date/daily_tasks?before_date=${beforeDate}&confirm=true`);
                          setMessage({ type: 'success', text: `${res.data.archived_count} bakım kaydı arşivlendi` });
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'warning'
                    });
                  }}
                  disabled={actionLoading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşivle + Sil
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => openDirectDeleteDialog('park_bakim', 'Park Bakım Yönetimi')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Kalıcı Sil (Arşivsiz)
                </Button>
              </CardContent>
            </Card>

            {/* Pasif Alanlar */}
            <Card className="border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-5 w-5 text-gray-600" />
                  Pasif Alanlar
                </CardTitle>
                <CardDescription>Faaliyet ve envanter geçmişini arşivle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih öncesi:</Label>
                  <div className="flex-1">
                    <DatePicker
                      value={archiveDates.passive_areas}
                      onChange={setArchiveDate('passive_areas')}
                      max={todayYmd()}
                      placeholder="Tarih seç"
                    />
                  </div>
                </div>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={async () => {
                    const beforeDate = archiveDates.passive_areas;
                    if (!beforeDate) {
                      setMessage({ type: 'error', text: 'Lütfen bir tarih seçin' });
                      return;
                    }
                    setConfirmDialog({
                      isOpen: true,
                      title: '📁 Pasif Alan Faaliyetlerini Arşivle',
                      description: `${formatTRDate(beforeDate)} tarihinden önceki tüm pasif alan faaliyetleri arşivlenecek.`,
                      warningText: 'Veriler arşive taşınacak ve aktif listeden silinecek.',
                      actionText: 'Arşivle + Sil',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.post(`${API}/api/data-management/archive-by-date/passive_area_activities?before_date=${beforeDate}&confirm=true`);
                          setMessage({ type: 'success', text: `${res.data.archived_count} faaliyet kaydı arşivlendi` });
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'warning'
                    });
                  }}
                  disabled={actionLoading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşivle + Sil
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => openDirectDeleteDialog('pasif_alanlar', 'Pasif Alanlar')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Kalıcı Sil (Arşivsiz)
                </Button>
              </CardContent>
            </Card>

            {/* İzin Yönetimi */}
            <Card className="border-blue-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  İzin Yönetimi
                </CardTitle>
                <CardDescription>İzin taleplerini arşivle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih öncesi:</Label>
                  <div className="flex-1">
                    <DatePicker
                      value={archiveDates.leave_requests}
                      onChange={setArchiveDate('leave_requests')}
                      max={todayYmd()}
                      placeholder="Tarih seç"
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={async () => {
                    const beforeDate = archiveDates.leave_requests;
                    if (!beforeDate) {
                      setMessage({ type: 'error', text: 'Lütfen bir tarih seçin' });
                      return;
                    }
                    setConfirmDialog({
                      isOpen: true,
                      title: '📁 İzin Taleplerini Arşivle',
                      description: `${formatTRDate(beforeDate)} tarihinden önceki tüm izin talepleri arşivlenecek.`,
                      warningText: 'Veriler arşive taşınacak ve aktif listeden silinecek.',
                      actionText: 'Arşivle + Sil',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.post(`${API}/api/data-management/archive-by-date/leave_requests?before_date=${beforeDate}&confirm=true`);
                          setMessage({ type: 'success', text: `${res.data.archived_count} izin talebi arşivlendi` });
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'warning'
                    });
                  }}
                  disabled={actionLoading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşivle + Sil
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => openDirectDeleteDialog('izin_yonetimi', 'İzin Yönetimi')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Kalıcı Sil (Arşivsiz)
                </Button>
              </CardContent>
            </Card>

            {/* Revize Yönetimi */}
            <Card className="border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Wrench className="h-5 w-5 text-orange-600" />
                  Revize Yönetimi
                </CardTitle>
                <CardDescription>Tamamlanan revizeleri arşivle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih öncesi:</Label>
                  <div className="flex-1">
                    <DatePicker
                      value={archiveDates.revisions}
                      onChange={setArchiveDate('revisions')}
                      max={todayYmd()}
                      placeholder="Tarih seç"
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  onClick={async () => {
                    const beforeDate = archiveDates.revisions;
                    if (!beforeDate) {
                      setMessage({ type: 'error', text: 'Lütfen bir tarih seçin' });
                      return;
                    }
                    setConfirmDialog({
                      isOpen: true,
                      title: '📁 Revizeleri Arşivle',
                      description: `${formatTRDate(beforeDate)} tarihinden önce tamamlanan tüm revizeler arşivlenecek.`,
                      warningText: 'Sadece "Tamamlandı" durumundaki revizeler arşivlenir.',
                      actionText: 'Arşivle + Sil',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.post(`${API}/api/data-management/archive-by-date/revisions?before_date=${beforeDate}&confirm=true`);
                          setMessage({ type: 'success', text: `${res.data.archived_count} revize arşivlendi` });
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'warning'
                    });
                  }}
                  disabled={actionLoading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşivle + Sil
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => openDirectDeleteDialog('revize_yonetimi', 'Revize Yönetimi')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Kalıcı Sil (Arşivsiz)
                </Button>
              </CardContent>
            </Card>

            {/* Malzeme Yönetimi */}
            <Card className="border-purple-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Package className="h-5 w-5 text-purple-600" />
                  Malzeme Yönetimi
                </CardTitle>
                <CardDescription>Malzeme taleplerini arşivle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih öncesi:</Label>
                  <div className="flex-1">
                    <DatePicker
                      value={archiveDates.material_requests}
                      onChange={setArchiveDate('material_requests')}
                      max={todayYmd()}
                      placeholder="Tarih seç"
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={async () => {
                    const beforeDate = archiveDates.material_requests;
                    if (!beforeDate) {
                      setMessage({ type: 'error', text: 'Lütfen bir tarih seçin' });
                      return;
                    }
                    setConfirmDialog({
                      isOpen: true,
                      title: '📁 Malzeme Taleplerini Arşivle',
                      description: `${formatTRDate(beforeDate)} tarihinden önceki tüm malzeme talepleri arşivlenecek.`,
                      warningText: 'Veriler arşive taşınacak ve aktif listeden silinecek.',
                      actionText: 'Arşivle + Sil',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.post(`${API}/api/data-management/archive-by-date/material_requests?before_date=${beforeDate}&confirm=true`);
                          setMessage({ type: 'success', text: `${res.data.archived_count} malzeme talebi arşivlendi` });
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'warning'
                    });
                  }}
                  disabled={actionLoading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşivle + Sil
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => openDirectDeleteDialog('malzeme_yonetimi', 'Malzeme Yönetimi')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Kalıcı Sil (Arşivsiz)
                </Button>
              </CardContent>
            </Card>

            {/* Mesajlar */}
            <Card className="border-cyan-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="h-5 w-5 text-cyan-600" />
                  Mesajlar
                </CardTitle>
                <CardDescription>Mesaj geçmişini arşivle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih öncesi:</Label>
                  <div className="flex-1">
                    <DatePicker
                      value={archiveDates.messages}
                      onChange={setArchiveDate('messages')}
                      max={todayYmd()}
                      placeholder="Tarih seç"
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  onClick={async () => {
                    const beforeDate = archiveDates.messages;
                    if (!beforeDate) {
                      setMessage({ type: 'error', text: 'Lütfen bir tarih seçin' });
                      return;
                    }
                    setConfirmDialog({
                      isOpen: true,
                      title: '📁 Mesajları Arşivle',
                      description: `${formatTRDate(beforeDate)} tarihinden önceki tüm mesajlar arşivlenecek.`,
                      warningText: 'Veriler arşive taşınacak ve aktif listeden silinecek.',
                      actionText: 'Arşivle + Sil',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.post(`${API}/api/data-management/archive-by-date/messages?before_date=${beforeDate}&confirm=true`);
                          setMessage({ type: 'success', text: `${res.data.archived_count} mesaj arşivlendi` });
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'warning'
                    });
                  }}
                  disabled={actionLoading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşivle + Sil
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => openDirectDeleteDialog('mesajlar', 'Mesajlar')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Kalıcı Sil (Arşivsiz)
                </Button>
              </CardContent>
            </Card>

            {/* Konum Geçmişi */}
            <Card className="border-rose-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-5 w-5 text-rose-600" />
                  Konum Geçmişi
                </CardTitle>
                <CardDescription>Personel konum loglarını arşivle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih öncesi:</Label>
                  <div className="flex-1">
                    <DatePicker
                      value={archiveDates.location_logs}
                      onChange={setArchiveDate('location_logs')}
                      max={todayYmd()}
                      placeholder="Tarih seç"
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-rose-600 hover:bg-rose-700"
                  onClick={async () => {
                    const beforeDate = archiveDates.location_logs;
                    if (!beforeDate) {
                      setMessage({ type: 'error', text: 'Lütfen bir tarih seçin' });
                      return;
                    }
                    setConfirmDialog({
                      isOpen: true,
                      title: '📁 Konum Geçmişini Arşivle',
                      description: `${formatTRDate(beforeDate)} tarihinden önceki tüm konum logları arşivlenecek.`,
                      warningText: 'Veriler arşive taşınacak ve aktif listeden silinecek. Geçmişe bakabilirsiniz.',
                      actionText: 'Arşivle + Sil',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.post(`${API}/api/data-management/archive-by-date/location_logs?before_date=${beforeDate}&confirm=true`);
                          setMessage({ type: 'success', text: `${res.data.archived_count} konum kaydı arşivlendi` });
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'warning'
                    });
                  }}
                  disabled={actionLoading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşivle + Sil
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => openDirectDeleteDialog('konum_takibi', 'Konum Takibi')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Kalıcı Sil (Arşivsiz)
                </Button>
              </CardContent>
            </Card>

            {/* Görev Medyaları */}
            <Card className="border-amber-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Camera className="h-5 w-5 text-amber-600" />
                  Görev Medyaları
                </CardTitle>
                <CardDescription>Bakım fotoğraf/videolarını arşivle</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih öncesi:</Label>
                  <div className="flex-1">
                    <DatePicker
                      value={archiveDates.task_media}
                      onChange={setArchiveDate('task_media')}
                      max={todayYmd()}
                      placeholder="Tarih seç"
                    />
                  </div>
                </div>
                <Button
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  onClick={async () => {
                    const beforeDate = archiveDates.task_media;
                    if (!beforeDate) {
                      setMessage({ type: 'error', text: 'Lütfen bir tarih seçin' });
                      return;
                    }
                    setConfirmDialog({
                      isOpen: true,
                      title: '📁 Görev Medyalarını Arşivle',
                      description: `${formatTRDate(beforeDate)} tarihinden önceki tüm fotoğraf/video kayıtları arşivlenecek.`,
                      warningText: 'Medya dosyaları arşive taşınacak. Disk alanı açılacak.',
                      actionText: 'Arşivle + Sil',
                      onConfirm: async () => {
                        try {
                          setActionLoading(true);
                          const res = await axios.post(`${API}/api/data-management/archive-by-date/task_media?before_date=${beforeDate}&confirm=true`);
                          setMessage({ type: 'success', text: `${res.data.archived_count} medya kaydı arşivlendi` });
                          fetchData();
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                        } finally {
                          setActionLoading(false);
                        }
                      },
                      type: 'warning'
                    });
                  }}
                  disabled={actionLoading}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşivle + Sil
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => openDirectDeleteDialog('gorev_medyalari', 'Görev Medyaları')}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Kalıcı Sil (Arşivsiz)
                </Button>
              </CardContent>
            </Card>

          </div>
          <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-orange-600" />
                Sistem Logları Temizliği
              </CardTitle>
              <CardDescription>
                Konum logları ve bildirimler arşivlenmez, direkt silinir (ayarlardaki gün sayısına göre)
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Konum Logları */}
              <div className="p-4 bg-white rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="h-4 w-4 text-orange-600" />
                      <span className="font-medium text-sm">Konum Logları</span>
                      <Badge variant="outline" className="text-xs">
                        {stats?.stats?.location_logs?.active_count || 0} kayıt
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">
                      {settings?.location_logs_delete_days || 7} günden eski loglar silinir
                    </p>
                  </div>
                  <Button 
                    size="sm"
                    variant="outline"
                    className="border-orange-300 text-orange-700"
                    onClick={() => setConfirmDialog({
                      isOpen: true,
                      title: '🗑️ Konum Loglarını Sil',
                      description: `${settings?.location_logs_delete_days || 7} günden eski loglar silinecek.`,
                      warningText: 'Bu işlem geri alınamaz!',
                      actionText: 'Sil',
                      onConfirm: cleanupLocationLogs,
                      type: 'warning'
                    })}
                    disabled={actionLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Bildirimler */}
              <div className="p-4 bg-white rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Bell className="h-4 w-4 text-orange-600" />
                      <span className="font-medium text-sm">Bildirimler</span>
                      <Badge variant="outline" className="text-xs">
                        {stats?.stats?.notifications?.active_count || 0} bildirim
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">
                      {settings?.notifications_delete_days || 90} günden eski bildirimler silinir
                    </p>
                  </div>
                  <Button 
                    size="sm"
                    variant="outline"
                    className="border-orange-300 text-orange-700"
                    onClick={() => setConfirmDialog({
                      isOpen: true,
                      title: '🔔 Bildirimleri Sil',
                      description: `${settings?.notifications_delete_days || 90} günden eski bildirimler silinecek.`,
                      warningText: 'Önemli bildirimler korunur.',
                      actionText: 'Sil',
                      onConfirm: cleanupNotifications,
                      type: 'warning'
                    })}
                    disabled={actionLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RAPORLAR */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                Raporlama Merkezi
              </CardTitle>
              <CardDescription>
                Sistem verilerini Excel ve PDF formatında indirin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                
                {/* Yıllık İzin Durumu Raporu */}
                <Card className="border-2 border-green-200 hover:border-green-400 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Calendar className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Yıllık İzin Durumu</h3>
                        <p className="text-xs text-gray-500">Tüm personelin izin durumu</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Personelin kalan/kullanılan izin günleri, kritik seviyeler
                    </p>
                    <div className="flex gap-2">
                      <a 
                        href={`${API}/reports/leave-status?format=excel`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button size="sm" className="w-full bg-green-600 hover:bg-green-700">
                          <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                        </Button>
                      </a>
                      <a 
                        href={`${API}/reports/leave-status?format=pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button size="sm" variant="outline" className="w-full border-green-600 text-green-600 hover:bg-green-50">
                          <FileText className="h-4 w-4 mr-1" /> PDF
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Personel Listesi Raporu */}
                <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Personel Listesi</h3>
                        <p className="text-xs text-gray-500">Tüm personel bilgileri</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Roller, bağlı üstler, atanmış parklar, iletişim bilgileri
                    </p>
                    <div className="flex gap-2">
                      <a href={`${API}/reports/personnel-list?format=excel`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700">
                          <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                        </Button>
                      </a>
                      <a href={`${API}/reports/personnel-list?format=pdf`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50">
                          <FileText className="h-4 w-4 mr-1" /> PDF
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Aylık Özet Raporu */}
                <Card className="border-2 border-orange-200 hover:border-orange-400 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <ClipboardList className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Aylık Özet</h3>
                        <p className="text-xs text-gray-500">İzin istatistikleri</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Onay/red oranları, en çok izin kullananlar, tür dağılımı
                    </p>
                    <div className="flex gap-2">
                      <a href={`${API}/reports/monthly-summary?format=excel`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700">
                          <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                        </Button>
                      </a>
                      <a href={`${API}/reports/monthly-summary?format=pdf`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" variant="outline" className="w-full border-orange-600 text-orange-600 hover:bg-orange-50">
                          <FileText className="h-4 w-4 mr-1" /> PDF
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Park Bakım Raporu */}
                <Card className="border-2 border-emerald-200 hover:border-emerald-400 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <TreePine className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Park Bakım</h3>
                        <p className="text-xs text-gray-500">Bakım istatistikleri</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Sulama, çim biçme, temizlik, budama sayıları park bazında
                    </p>
                    <div className="flex gap-2">
                      <a href={`${API}/reports/park-maintenance?format=excel`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700">
                          <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                        </Button>
                      </a>
                      <a href={`${API}/reports/park-maintenance?format=pdf`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" variant="outline" className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                          <FileText className="h-4 w-4 mr-1" /> PDF
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Malzeme Talep Raporu */}
                <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Package className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Malzeme Talepleri</h3>
                        <p className="text-xs text-gray-500">Talep ve teslim durumu</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Malzeme türleri, miktarlar, onay ve teslim durumları
                    </p>
                    <div className="flex gap-2">
                      <a href={`${API}/reports/material-requests?format=excel`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700">
                          <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                        </Button>
                      </a>
                      <a href={`${API}/reports/material-requests?format=pdf`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" variant="outline" className="w-full border-purple-600 text-purple-600 hover:bg-purple-50">
                          <FileText className="h-4 w-4 mr-1" /> PDF
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Görev Tamamlama Raporu */}
                <Card className="border-2 border-cyan-200 hover:border-cyan-400 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-cyan-100 rounded-lg">
                        <Wrench className="h-6 w-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Görev Tamamlama</h3>
                        <p className="text-xs text-gray-500">Görev durumları</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Atanan görevler, tamamlanma oranları, gecikmeler
                    </p>
                    <div className="flex gap-2">
                      <a href={`${API}/reports/task-completion?format=excel`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" className="w-full bg-cyan-600 hover:bg-cyan-700">
                          <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                        </Button>
                      </a>
                      <a href={`${API}/reports/task-completion?format=pdf`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button size="sm" variant="outline" className="w-full border-cyan-600 text-cyan-600 hover:bg-cyan-50">
                          <FileText className="h-4 w-4 mr-1" /> PDF
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>

              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* YEDEKLEME */}
        <TabsContent value="backup" className="space-y-4">
          {/* Yeni Yedek Oluştur */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-blue-600" />
                Veritabanı Yedeği Oluştur
              </CardTitle>
              <CardDescription>
                Tüm veritabanı verilerini ZIP formatında yedekleyin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="backup-desc">Yedek Açıklaması (Opsiyonel)</Label>
                  <Input
                    id="backup-desc"
                    value={backupDescription}
                    onChange={(e) => setBackupDescription(e.target.value)}
                    placeholder="Örn: Haftalık yedek, Güncelleme öncesi yedek..."
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={createBackup} 
                    disabled={backupLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {backupLoading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Archive className="h-4 w-4 mr-2" />
                    )}
                    Yedek Oluştur
                  </Button>
                </div>
              </div>
              
              <Alert>
                <Database className="h-4 w-4" />
                <AlertDescription>
                  Yedek oluşturma işlemi tüm koleksiyonları (personel, parklar, izin talepleri, mesajlar vb.) içerir.
                  İşlem veri miktarına bağlı olarak birkaç saniye sürebilir.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Mevcut Yedekler */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-green-600" />
                  Mevcut Yedekler
                </span>
                <Button variant="outline" size="sm" onClick={fetchBackups}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Yenile
                </Button>
              </CardTitle>
              <CardDescription>
                {backups.length} adet yedek dosyası mevcut
              </CardDescription>
            </CardHeader>
            <CardContent>
              {backups.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Archive className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>Henüz yedek dosyası bulunmuyor</p>
                  <p className="text-sm mt-1">Yukarıdaki butona tıklayarak ilk yedeğinizi oluşturun</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dosya Adı</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Boyut</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.map((backup) => (
                      <TableRow key={backup.filename}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-400" />
                            {backup.filename}
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(backup.created_at).toLocaleString('tr-TR')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{backup.size_mb} MB</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => getBackupInfo(backup.filename)}
                              title="Detaylar"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadBackup(backup.filename)}
                              title="İndir"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-orange-600 hover:text-orange-700"
                              onClick={() => setConfirmDialog({
                                isOpen: true,
                                title: 'Yedeği Geri Yükle',
                                description: `"${backup.filename}" yedeğini geri yüklemek istediğinize emin misiniz?`,
                                warningText: 'Bu işlem mevcut verilerinizi değiştirebilir.',
                                actionText: 'Geri Yükle',
                                onConfirm: () => restoreBackup(backup.filename),
                                type: 'warning'
                              })}
                              title="Geri Yükle"
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => setConfirmDialog({
                                isOpen: true,
                                title: 'Yedeği Sil',
                                description: `"${backup.filename}" yedeğini silmek istediğinize emin misiniz?`,
                                warningText: 'Bu işlem geri alınamaz!',
                                actionText: 'Sil',
                                onConfirm: () => deleteBackup(backup.filename),
                                type: 'danger'
                              })}
                              title="Sil"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Geri Yükleme Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-orange-600" />
                Geri Yükleme Ayarları
              </CardTitle>
              <CardDescription>
                Yedeği geri yüklerken kullanılacak mod
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Birleştirme Modu (Merge)</p>
                    <p className="text-sm text-gray-500">Mevcut verilerle birleştirir, aynı ID varsa günceller</p>
                  </div>
                  <input
                    type="radio"
                    name="restoreMode"
                    value="merge"
                    checked={restoreMode === 'merge'}
                    onChange={(e) => setRestoreMode(e.target.value)}
                    className="h-4 w-4"
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg border-red-200 bg-red-50">
                  <div>
                    <p className="font-medium text-red-700">Değiştirme Modu (Replace)</p>
                    <p className="text-sm text-red-600">⚠️ Tüm mevcut verileri siler, yedeği tamamen yükler</p>
                  </div>
                  <input
                    type="radio"
                    name="restoreMode"
                    value="replace"
                    checked={restoreMode === 'replace'}
                    onChange={(e) => setRestoreMode(e.target.value)}
                    className="h-4 w-4"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Yedek Detay Dialogu */}
          <Dialog open={!!selectedBackupInfo} onOpenChange={() => setSelectedBackupInfo(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Yedek Detayları
                </DialogTitle>
              </DialogHeader>
              {selectedBackupInfo && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Dosya Adı</Label>
                      <p className="font-medium">{selectedBackupInfo.filename}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Boyut</Label>
                      <p className="font-medium">{selectedBackupInfo.size_mb} MB</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                      <p className="font-medium">
                        {new Date(selectedBackupInfo.created_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Toplam Kayıt</Label>
                      <p className="font-medium">{selectedBackupInfo.total_records?.toLocaleString('tr-TR') || 'N/A'}</p>
                    </div>
                  </div>
                  
                  {selectedBackupInfo.description && (
                    <div>
                      <Label className="text-gray-500">Açıklama</Label>
                      <p className="font-medium">{selectedBackupInfo.description}</p>
                    </div>
                  )}
                  
                  {selectedBackupInfo.collections && (
                    <div>
                      <Label className="text-gray-500">İçerdiği Koleksiyonlar</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedBackupInfo.collections.map((coll) => (
                          <Badge key={coll} variant="secondary" className="text-xs">
                            {coll}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedBackupInfo(null)}>
                  Kapat
                </Button>
                <Button 
                  onClick={() => {
                    downloadBackup(selectedBackupInfo.filename);
                    setSelectedBackupInfo(null);
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  İndir
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* AYARLAR */}
        <TabsContent value="settings" className="space-y-4">
          {settings && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Veri Saklama Ayarları
                </CardTitle>
                <CardDescription>
                  Otomatik arşivleme ve temizlik kurallarını yapılandırın
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Otomatik Temizlik */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Otomatik Temizlik</p>
                    <p className="text-sm text-gray-500">Sistem otomatik olarak temizlik yapar</p>
                  </div>
                  <Switch 
                    checked={settings.auto_cleanup_enabled}
                    onCheckedChange={(checked) => updateSettings({...settings, auto_cleanup_enabled: checked})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Konum Logları */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Konum Logları - Silme Süresi (gün)
                    </label>
                    <Input 
                      type="number"
                      value={settings.location_logs_delete_days}
                      onChange={(e) => setSettings({...settings, location_logs_delete_days: parseInt(e.target.value)})}
                      min={1}
                      max={365}
                    />
                  </div>

                  {/* Bildirimler */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      Bildirimler - Silme Süresi (gün)
                    </label>
                    <Input 
                      type="number"
                      value={settings.notifications_delete_days}
                      onChange={(e) => setSettings({...settings, notifications_delete_days: parseInt(e.target.value)})}
                      min={30}
                      max={365}
                    />
                  </div>

                  {/* Reddedilen Malzeme Arşivleme */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Reddedilen Malzeme - Arşivleme (gün)
                    </label>
                    <Input 
                      type="number"
                      value={settings.rejected_material_archive_days}
                      onChange={(e) => setSettings({...settings, rejected_material_archive_days: parseInt(e.target.value)})}
                      min={30}
                      max={365}
                    />
                  </div>

                  {/* Reddedilen Malzeme Silme */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Reddedilen Malzeme - Kalıcı Silme (gün)
                    </label>
                    <Input 
                      type="number"
                      value={settings.rejected_material_delete_days}
                      onChange={(e) => setSettings({...settings, rejected_material_delete_days: parseInt(e.target.value)})}
                      min={180}
                      max={730}
                    />
                  </div>

                  {/* Revize Arşivleme */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Tamamlanan Revize - Arşivleme (gün)
                    </label>
                    <Input 
                      type="number"
                      value={settings.revision_archive_after_days}
                      onChange={(e) => setSettings({...settings, revision_archive_after_days: parseInt(e.target.value)})}
                      min={30}
                      max={365}
                    />
                  </div>

                  {/* İzin Talepleri Kalıcı Silme */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      İzin Talepleri - Kalıcı Silme (yıl)
                    </label>
                    <Input 
                      type="number"
                      value={settings.leave_delete_after_years}
                      onChange={(e) => setSettings({...settings, leave_delete_after_years: parseInt(e.target.value)})}
                      min={1}
                      max={10}
                    />
                  </div>
                </div>

                <Button 
                  onClick={() => updateSettings(settings)}
                  disabled={actionLoading}
                  className="w-full"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Ayarları Kaydet
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* 2. ONAY DİALOG'U */}
      <Dialog open={confirmDialog.isOpen} onOpenChange={(open) => !open && setConfirmDialog({...confirmDialog, isOpen: false})}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${confirmDialog.type === 'danger' ? 'text-red-700' : 'text-orange-700'}`}>
              <ShieldAlert className={`h-6 w-6 ${confirmDialog.type === 'danger' ? 'text-red-600' : 'text-orange-600'}`} />
              {confirmDialog.title}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {confirmDialog.description}
            </DialogDescription>
          </DialogHeader>
          
          {/* Uyarı Kutusu */}
          <div className={`p-4 rounded-lg border-2 ${
            confirmDialog.type === 'danger' 
              ? 'bg-red-50 border-red-300' 
              : 'bg-orange-50 border-orange-300'
          }`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`h-6 w-6 mt-0.5 ${
                confirmDialog.type === 'danger' ? 'text-red-600' : 'text-orange-600'
              }`} />
              <div>
                <p className={`font-semibold ${
                  confirmDialog.type === 'danger' ? 'text-red-800' : 'text-orange-800'
                }`}>
                  {confirmDialog.type === 'danger' ? '⚠️ KRİTİK UYARI' : '⚠️ DİKKAT'}
                </p>
                <p className={`text-sm mt-1 ${
                  confirmDialog.type === 'danger' ? 'text-red-700' : 'text-orange-700'
                }`}>
                  {confirmDialog.warningText}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setConfirmDialog({...confirmDialog, isOpen: false})}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              İptal
            </Button>
            <Button 
              variant={confirmDialog.type === 'danger' ? 'destructive' : 'default'}
              onClick={async () => {
                if (confirmDialog.onConfirm) {
                  await confirmDialog.onConfirm();
                }
                setConfirmDialog({...confirmDialog, isOpen: false});
              }}
              disabled={actionLoading}
              className={`flex-1 ${confirmDialog.type === 'danger' ? '' : 'bg-orange-600 hover:bg-orange-700'}`}
            >
              {actionLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  İşleniyor...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {confirmDialog.actionText}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tam Yedek Geri Yükleme Onayı */}
      <Dialog open={fullRestoreConfirmOpen} onOpenChange={(open) => !open && setFullRestoreConfirmOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="h-6 w-6 text-red-600" />
              Tam Yedek Geri Yükleme
            </DialogTitle>
            <DialogDescription className="pt-2">
              Bu işlem veritabanını geri yükler. Devam etmek için <strong>YUKLE</strong> yazın.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Onay Metni</Label>
            <Input
              placeholder="YUKLE"
              value={fullRestoreConfirmText}
              onChange={(e) => setFullRestoreConfirmText(e.target.value.toUpperCase())}
            />
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setFullRestoreConfirmOpen(false)}>
              İptal
            </Button>
            <Button
              variant="destructive"
              disabled={fullRestoreConfirmText !== 'YUKLE' || fullBackupLoading}
              onClick={restoreFullBackup}
            >
              Geri Yükle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ÇİFT UYARILI KALICI SİLME DİALOG'U */}
      <Dialog open={directDeleteDialog.isOpen} onOpenChange={(open) => !open && setDirectDeleteDialog({ isOpen: false, step: 1, category: '', categoryName: '', confirmText: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="h-6 w-6 text-red-600" />
              {directDeleteDialog.step === 1 ? '⚠️ Kalıcı Silme Uyarısı' : '🛑 Son Onay'}
            </DialogTitle>
          </DialogHeader>
          
          {directDeleteDialog.step === 1 ? (
            /* ADIM 1: İlk Uyarı */
            <>
              <div className="p-4 rounded-lg border-2 bg-red-50 border-red-300">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-6 w-6 mt-0.5 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-800">KRİTİK UYARI!</p>
                    <p className="text-sm mt-1 text-red-700">
                      <strong>{directDeleteDialog.categoryName}</strong> kategorisindeki TÜM veriler kalıcı olarak silinecek.
                    </p>
                    <ul className="text-sm mt-2 text-red-600 list-disc list-inside">
                      <li>Bu işlem GERİ ALINAMAZ</li>
                      <li>Veriler arşivlenmeden silinecek</li>
                      {directDeleteDialog.category === 'izin_yonetimi' && (
                        <li>Tüm personelin izin sayaçları sıfırlanacak</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setDirectDeleteDialog({ isOpen: false, step: 1, category: '', categoryName: '', confirmText: '' })}
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  İptal
                </Button>
                <Button 
                  variant="destructive"
                  onClick={proceedToStep2}
                  className="flex-1"
                >
                  Devam Et →
                </Button>
              </DialogFooter>
            </>
          ) : (
            /* ADIM 2: Yazılı Onay */
            <>
              <div className="p-4 rounded-lg border-2 bg-red-100 border-red-400">
                <p className="text-center text-red-800 font-semibold mb-3">
                  Bu işlemi onaylamak için aşağıya <span className="bg-red-200 px-2 py-1 rounded font-mono">SIFIRLA</span> yazın:
                </p>
                <Input
                  value={directDeleteDialog.confirmText}
                  onChange={(e) => setDirectDeleteDialog(prev => ({ ...prev, confirmText: e.target.value.toUpperCase() }))}
                  placeholder="SIFIRLA"
                  className="text-center font-mono text-lg border-red-300 focus:border-red-500"
                  autoFocus
                />
              </div>
              
              <DialogFooter className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setDirectDeleteDialog(prev => ({ ...prev, step: 1, confirmText: '' }))}
                  className="flex-1"
                >
                  ← Geri
                </Button>
                <Button 
                  variant="destructive"
                  onClick={executeDirectDelete}
                  disabled={directDeleteDialog.confirmText !== 'SIFIRLA' || actionLoading}
                  className="flex-1"
                >
                  {actionLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Siliniyor...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Kalıcı Olarak Sil
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Arşiv Ön İzleme Dialog */}
      <Dialog open={previewDialog.isOpen} onOpenChange={(open) => setPreviewDialog({ ...previewDialog, isOpen: open })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Arşiv Kaydı Ön İzleme
            </DialogTitle>
            <DialogDescription>
              Bu kaydın tüm detaylarını inceleyebilir, geri yükleyebilir veya kalıcı olarak silebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          
          {previewDialog.item && (
            <div className="space-y-4">
              {/* Arşiv Bilgisi */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-amber-800 text-sm">
                  <Archive className="h-4 w-4" />
                  <span>
                    <strong>Arşivlenme Tarihi:</strong> {previewDialog.item.archived_at ? new Date(previewDialog.item.archived_at).toLocaleString('tr-TR') : '-'}
                  </span>
                </div>
                {previewDialog.item.archive_reason && (
                  <div className="text-xs text-amber-600 mt-1">
                    Sebep: {previewDialog.item.archive_reason}
                  </div>
                )}
              </div>

              {/* Kayıt Detayları */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b">
                  <span className="font-medium text-sm">Kayıt Detayları</span>
                </div>
                <div className="p-3 space-y-2">
                  {Object.entries(previewDialog.item)
                    .filter(([key, value]) => {
                      // Gizlenecek teknik alanlar
                      const hiddenFields = [
                        '_id', 'id', 'archived_at', 'archived_from', 'archive_reason', 'restored_at',
                        'target_region_ids', 'region_ids', 'assigned_personnel_ids', 'attachments',
                        'read_by', 'sse_sent', 'notification_sent', 'is_broadcast', 'is_important',
                        'weather_condition', 'media_urls', 'task_ids', 'completion_id', 'sync_status',
                        'personnel_id', 'park_id', 'region_id', 'sender_id', 'receiver_id', 'approver_id',
                        'assigned_to_id', 'created_by_id', 'foreman_id', 'mudur_yardimcisi_id',
                        'original_collection', 'current_approver_id', 'current_approver_role',
                        'approval_chain', 'weekday_count', 'weekend_count', 'approval_history'
                      ];
                      // ID alanlarını gizle (name alanı varsa)
                      if (key.endsWith('_id') && key !== 'id') {
                        const nameKey = key.replace('_id', '_name');
                        if (previewDialog.item[nameKey]) return false;
                      }
                      // Boş array ve objeler gösterme
                      if (Array.isArray(value) && value.length === 0) return false;
                      if (typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length === 0) return false;
                      // UUID içeren array'leri gizle
                      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string' && value[0].match(/^[0-9a-f]{8}-[0-9a-f]{4}-/i)) return false;
                      // Tekil UUID değerleri gizle
                      if (typeof value === 'string' && value.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) return false;
                      
                      return !hiddenFields.includes(key);
                    })
                    .map(([key, value]) => {
                      // Tarih alanlarını formatla
                      let displayValue = value;
                      if (typeof value === 'string' && (key.includes('date') || key.includes('_at') || key.includes('timestamp'))) {
                        try {
                          const date = new Date(value);
                          if (!isNaN(date.getTime())) {
                            displayValue = date.toLocaleString('tr-TR');
                          }
                        } catch (e) {
                          displayValue = value;
                        }
                      } else if (Array.isArray(value)) {
                        // Array'leri virgülle ayır
                        displayValue = value.join(', ');
                      } else if (typeof value === 'object' && value !== null) {
                        // Objeleri düzgün göster
                        displayValue = Object.entries(value)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(', ');
                      } else if (typeof value === 'boolean') {
                        displayValue = value ? 'Evet' : 'Hayır';
                      }
                      
                      // Status değerlerini Türkçeleştir
                      if (key === 'status') {
                        const statusLabels = {
                          'pending': 'Bekliyor',
                          'approved': 'Onaylandı',
                          'rejected': 'Reddedildi',
                          'completed': 'Tamamlandı',
                          'in_progress': 'Devam Ediyor',
                          'cancelled': 'İptal Edildi'
                        };
                        displayValue = statusLabels[value] || value;
                      }
                      
                      // Leave type değerlerini Türkçeleştir
                      if (key === 'leave_type' || key === 'reason_type') {
                        const leaveLabels = {
                          'annual': 'Yıllık İzin',
                          'yillik': 'Yıllık İzin',
                          'sick': 'Hastalık İzni',
                          'rapor': 'Rapor',
                          'social': 'Sosyal İzin',
                          'sosyal': 'Sosyal İzin',
                          'unpaid': 'Ücretsiz İzin',
                          'marriage': 'Evlilik İzni',
                          'death': 'Ölüm İzni',
                          'diger': 'Diğer',
                          'other': 'Diğer'
                        };
                        displayValue = leaveLabels[value] || value;
                      }
                      
                      // Message type değerlerini Türkçeleştir
                      if (key === 'message_type' || key === 'type') {
                        const typeLabels = {
                          'announcement': 'Duyuru',
                          'message': 'Mesaj',
                          'notification': 'Bildirim',
                          'alert': 'Uyarı',
                          'info': 'Bilgi',
                          'task': 'Görev',
                          'leave': 'İzin',
                          'material': 'Malzeme',
                          'revision': 'Revize'
                        };
                        displayValue = typeLabels[value] || value;
                      }
                      
                      // Role değerlerini Türkçeleştir
                      if (key === 'sender_role' || key === 'role' || key === 'target_roles' || key === 'user_role' || key === 'personnel_role') {
                        const roleLabels = {
                          'admin': 'Yönetici',
                          'mudur': 'Müdür',
                          'mudur_yardimcisi': 'Müdür Yardımcısı',
                          'bolge_muhendisi': 'Bölge Mühendisi',
                          'formen': 'Formen',
                          'park_gorevlisi': 'Park Görevlisi',
                          'cim_ekibi': 'Çim Ekibi'
                        };
                        // Eğer değer array ise (target_roles gibi)
                        if (Array.isArray(value)) {
                          displayValue = value.map(v => roleLabels[v] || v).join(', ');
                        } else {
                          displayValue = roleLabels[value] || value;
                        }
                      }
                      
                      // Priority değerlerini Türkçeleştir
                      if (key === 'priority') {
                        const priorityLabels = {
                          'low': 'Düşük',
                          'normal': 'Normal',
                          'high': 'Yüksek',
                          'urgent': 'Acil'
                        };
                        displayValue = priorityLabels[value] || value;
                      }
                      
                      // Key isimlerini Türkçeleştir
                      const keyLabels = {
                        personnel_name: 'Personel',
                        personnel_role: 'Personel Rolü',
                        park_name: 'Park',
                        region_name: 'Bölge',
                        title: 'Başlık',
                        subject: 'Konu',
                        content: 'İçerik',
                        message: 'Mesaj',
                        body: 'İçerik',
                        status: 'Durum',
                        type: 'Tür',
                        message_type: 'Mesaj Türü',
                        sender_role: 'Gönderen Rolü',
                        target_roles: 'Hedef Roller',
                        recipient_count: 'Alıcı Sayısı',
                        user_role: 'Kullanıcı Rolü',
                        leave_type: 'İzin Türü',
                        start_date: 'Başlangıç Tarihi',
                        end_date: 'Bitiş Tarihi',
                        return_date: 'Dönüş Tarihi',
                        total_days: 'Toplam Gün',
                        weekdays: 'Hafta İçi Gün',
                        weekends: 'Hafta Sonu Gün',
                        workdays_used: 'Kullanılan İş Günü',
                        created_at: 'Oluşturulma Tarihi',
                        updated_at: 'Güncellenme Tarihi',
                        approved_at: 'Onay Tarihi',
                        final_decision_at: 'Son Karar Tarihi',
                        cancelled_at: 'İptal Tarihi',
                        completion_date: 'Tamamlanma Tarihi',
                        activity_date: 'Faaliyet Tarihi',
                        timestamp: 'Zaman',
                        sender_name: 'Gönderen',
                        receiver_name: 'Alıcı',
                        area_name: 'Alan Adı',
                        quantity: 'Miktar',
                        unit: 'Birim',
                        description: 'Açıklama',
                        reason: 'Sebep',
                        reason_type: 'İzin Sebebi',
                        reason_detail: 'Detay',
                        notes: 'Notlar',
                        comment: 'Yorum',
                        latitude: 'Enlem',
                        longitude: 'Boylam',
                        file_url: 'Dosya',
                        file_type: 'Dosya Türü',
                        report_file: 'Rapor Dosyası',
                        report_filename: 'Dosya Adı',
                        material_name: 'Malzeme Adı',
                        requested_quantity: 'Talep Edilen Miktar',
                        approved_quantity: 'Onaylanan Miktar',
                        approver_name: 'Onaylayan',
                        rejection_reason: 'Red Sebebi',
                        priority: 'Öncelik',
                        activity_type: 'Faaliyet Türü',
                        completed: 'Tamamlandı mı',
                        is_read: 'Okundu mu',
                        used_leave_days: 'Kullanılan İzin Günü',
                        remaining_leave_days: 'Kalan İzin Günü'
                      };
                      
                      return (
                        <div key={key} className="flex border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                          <span className="text-gray-500 text-sm w-36 flex-shrink-0 font-medium">
                            {keyLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </span>
                          <span className="text-sm break-all flex-1">
                            {displayValue !== null && displayValue !== undefined && displayValue !== '' 
                              ? String(displayValue) 
                              : <span className="text-gray-400 italic">-</span>}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
              
              {/* Onay Süreci - approval_history varsa göster */}
              {previewDialog.item.approval_history && previewDialog.item.approval_history.length > 0 && (
                <div className="border rounded-lg overflow-hidden mt-3">
                  <div className="bg-blue-50 px-3 py-2 border-b">
                    <span className="font-medium text-sm text-blue-800">📋 Onay Süreci</span>
                  </div>
                  <div className="p-3">
                    <div className="space-y-3">
                      {/* Duplicate kayıtları filtrele - aynı kişi, aynı aksiyon, 1 dakika içinde */}
                      {previewDialog.item.approval_history
                        .filter((step, index, arr) => {
                          if (index === 0) return true;
                          const prev = arr[index - 1];
                          // Aynı kişi ve aynı aksiyon mu?
                          if (step.approver_id === prev.approver_id && step.action === prev.action) {
                            // 1 dakika içinde mi?
                            const currTime = new Date(step.timestamp).getTime();
                            const prevTime = new Date(prev.timestamp).getTime();
                            if (Math.abs(currTime - prevTime) < 60000) { // 60 saniye
                              return false; // Duplicate, gösterme
                            }
                          }
                          return true;
                        })
                        .map((step, index) => {
                        const actionLabels = {
                          'forward': { text: 'Üst Makama Sevk Etti', color: 'text-amber-600', bg: 'bg-amber-50', icon: '↗️' },
                          'escalate': { text: 'Üst Makama Sevk Etti', color: 'text-amber-600', bg: 'bg-amber-50', icon: '↗️' },
                          'reject_and_forward': { text: 'Reddet ile Üst Makama Sevk Etti.', color: 'text-red-600', bg: 'bg-red-50', icon: '❌' },
                          'approve': { text: 'Onayladı', color: 'text-green-600', bg: 'bg-green-50', icon: '✅' },
                          'reject': { text: 'Reddetti', color: 'text-red-600', bg: 'bg-red-50', icon: '❌' },
                          'cancel': { text: 'İptal Etti', color: 'text-gray-600', bg: 'bg-gray-50', icon: '🚫' }
                        };
                        const roleLabels = {
                          'mudur': 'Müdür',
                          'mudur_yardimcisi': 'Müdür Yardımcısı',
                          'bolge_muhendisi': 'Bölge Mühendisi',
                          'formen': 'Formen',
                          'park_gorevlisi': 'Park Görevlisi'
                        };
                        const action = actionLabels[step.action] || { text: step.action, color: 'text-gray-600', bg: 'bg-gray-50', icon: '📝' };
                        const roleName = roleLabels[step.approver_role] || step.approver_role;
                        const timestamp = step.timestamp ? new Date(step.timestamp).toLocaleString('tr-TR') : '';
                        
                        return (
                          <div key={index} className={`p-2 rounded-lg ${action.bg} border`}>
                            <div className="flex items-start gap-2">
                              <span className="text-lg">{action.icon}</span>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">{step.approver_name}</span>
                                  <span className="text-xs text-gray-500">({roleName})</span>
                                  <span className={`text-xs font-medium ${action.color}`}>{action.text}</span>
                                </div>
                                {step.comment && (
                                  <div className="mt-1 text-sm text-gray-700 bg-white/50 p-1.5 rounded">
                                    💬 {step.comment}
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 mt-1">{timestamp}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Alan Açıklamaları - İzin Talepleri için */}
              {previewDialog.collection?.includes('leave') && (
                <div className="border rounded-lg overflow-hidden mt-3 bg-gray-50">
                  <div className="px-3 py-2 border-b bg-gray-100">
                    <span className="font-medium text-sm text-gray-600">ℹ️ Alan Açıklamaları</span>
                  </div>
                  <div className="p-3 text-xs text-gray-600 space-y-1">
                    <p><strong>İzin Sebebi:</strong> Yıllık izin, sosyal izin, rapor veya diğer izin türü</p>
                    <p><strong>Detay:</strong> İzin sebebi için ek açıklama (varsa)</p>
                    <p><strong>Rapor Dosyası:</strong> Hastalık izni için yüklenen rapor dosyası (varsa)</p>
                    <p><strong>Hafta İçi/Sonu Gün:</strong> İzin süresinin hafta içi ve hafta sonu dağılımı</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setPreviewDialog({ isOpen: false, item: null, collection: '' })}
            >
              <X className="h-4 w-4 mr-2" />
              Kapat
            </Button>
            <Button 
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
              onClick={() => {
                setPreviewDialog({ isOpen: false, item: null, collection: '' });
                setConfirmDialog({
                  isOpen: true,
                  title: '🔄 Geri Yükle',
                  description: 'Bu kayıt arşivden çıkarılıp aktif listeye geri yüklenecek.',
                  warningText: 'Kayıt tekrar aktif hale gelecek ve ilgili listede görünecek.',
                  actionText: 'Geri Yükle',
                  onConfirm: async () => {
                    try {
                      setActionLoading(true);
                      await axios.post(`${API}/api/data-management/restore-from-archive/${previewDialog.collection}/${previewDialog.item.id}`);
                      setMessage({ type: 'success', text: 'Kayıt başarıyla geri yüklendi' });
                      fetchArchivedData(previewDialog.collection);
                      fetchData();
                    } catch (error) {
                      setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                    } finally {
                      setActionLoading(false);
                    }
                  },
                  type: 'warning'
                });
              }}
              disabled={actionLoading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Geri Yükle
            </Button>
            <Button 
              variant="destructive"
              onClick={() => {
                setPreviewDialog({ isOpen: false, item: null, collection: '' });
                setConfirmDialog({
                  isOpen: true,
                  title: '🗑️ Kalıcı Sil',
                  description: 'Bu kayıt kalıcı olarak silinecek.',
                  warningText: 'BU İŞLEM GERİ ALINAMAZ! Silinen veri kurtarılamaz.',
                  actionText: 'KALICI SİL',
                  onConfirm: async () => {
                    try {
                      setActionLoading(true);
                      await axios.delete(`${API}/api/data-management/permanent-delete/${previewDialog.collection}?confirm_text=KALICI SIL&item_ids=${previewDialog.item.id}`);
                      setMessage({ type: 'success', text: 'Kayıt kalıcı olarak silindi' });
                      fetchArchivedData(previewDialog.collection);
                      fetchData();
                    } catch (error) {
                      setMessage({ type: 'error', text: 'Hata: ' + (error.response?.data?.detail || error.message) });
                    } finally {
                      setActionLoading(false);
                    }
                  },
                  type: 'danger'
                });
              }}
              disabled={actionLoading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Kalıcı Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManagementPanel;
